import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta as AngularMeta, Title as AngularTitle } from '@angular/platform-browser';
import { Config } from './app/universal-config';
import { isB2BOrgPredicate } from './model/organization.model';

/**
 * Represent meta element.
 *
 * ### Example
 *
 * ```ts
 * { name: 'application-name', content: 'Name of my application' },
 * { name: 'description', content: 'A description of the page', id: 'desc' }
 * // ...
 * // Twitter
 * { name: 'twitter:title', content: 'Content Title' }
 * // ...
 * // Google+
 * { itemprop: 'name', content: 'Content Title' },
 * { itemprop: 'description', content: 'Content Title' }
 * // ...
 * // Facebook / Open Graph
 * { property: 'fb:app_id', content: '123456789' },
 * { property: 'og:title', content: 'Content Title' }
 * ```
 *
 * @experimental
 */
export interface IMetaDefinition {
  charset?: string;
  content?: string;
  httpEquiv?: string;
  id?: string;
  itemprop?: string;
  name?: string;
  property?: string;
  scheme?: string;
  url?: string;
  [prop: string]: string;
}

/**
 * A service that can be used to get and add meta tags.
 *
 * @experimental
 */
@Injectable()
export class Meta {
  private isBrowser;
  private meta;
  private title;

  private NIMBUS_NOTE_TITLE = 'FuseBase';
  private NIMBUS_NOTE_SLOGAN = 'FuseBase. One place to collect information and collaborate on your work.';
  private NIMBUS_NOTE_FAVICON = require('./preview-icons/favicon-notes-app.png');
  private NIMBUS_NOTE_PREVIEW = require('./preview-icons/preview-placeholder-fusebase.png');

  private NIMBUS_CAPTURE_TITLE = 'FuseBase Capture';
  private NIMBUS_CAPTURE_SLOGAN = 'FuseBase Screenshot. Communicate & provide feedback faster!';
  private NIMBUS_CAPTURE_FAVICON = require('./preview-icons/favicon-notes-app.png');
  private NIMBUS_CAPTURE_PREVIEW = require('./preview-icons/preview-placeholder-fusebase.png');

  private CAPTURE_ROLES = [
    'screenshot',
    'screencast',
    'video',
    'streaming_video',
  ]

  constructor(
    @Inject(DOCUMENT) public _document: any,
    @Inject('isBrowser') isBrowser: boolean,
    private _config: Config
  ) {
    this.meta = new AngularMeta(_document);
    this.title = new AngularTitle(_document);
    this.isBrowser = isBrowser;
  }

  setMetaForNode (share, node, text, attachment) {
    this.setFavicon(share, node);
    this.setMetaUrl(this.getMetaUrlForShare(share));
    this.setMetaTitle(this.getTitleForNode(share, node));
    this.setMetaDescription(this.getDescriptionForNode(share, node, text));
    this.setMetaPreview(share, node, attachment);
    this.setMetaEmbedType(node);
  }

  setMetaForAttachment (share, note, attachment) {
    this.setFavicon(share, note);
    this.setMetaUrl(this.getMetaUrlForShare(share));
    this.setMetaTitle(this.getTitleForAttachment(share, attachment));
    this.setMetaDescription(this.getDescriptionForAttachment(share));
    this.setMetaPreview(share, note, attachment);
  }

  setMetaForError(organization) {
    if (isB2BOrgPredicate()(organization)) {
      this.setMetaTitle(organization.title);
    } else {
      this.setMetaTitle('FuseBase');
    }
    if(organization.smallLogoStoredFileUUID) {
      const share = {
        instance: {
          org: organization,
          brandingProfile: {
            sharesFaviconStoredFileUUID: organization.smallLogoStoredFileUUID
          }
        }
      }
      this.setFavicon(share, false);
    } else {
      this.updateFavicon(null);
    }
  }

  /**
   * Adds a new meta tag to the dom.
   *
   *  ### Example
   *
   * ```ts
   * const name: IMetaDefinition = {name: 'application-name', content: 'Name of my application'};
   * const desc: IMetaDefinition = {name: 'description', content: 'A description of the page'};
   * const tags: HTMLMetaElement[] = this.meta.addTags([name, desc]);
   * ```
   *
   * @param tags
   * @returns {HTMLMetaElement[]}
   */
  addTags(...tags: Array<IMetaDefinition|IMetaDefinition[]>): void {
    if (this.isBrowser) {
      return;
    }
    const presentTags = this._flattenArray(tags);
    if (presentTags.length === 0) {
      return;
    }
    this.meta.addTags(presentTags);
  }

  private _flattenArray(input: any[], out: any[] = []): any[] {
    if (input) {
      for (const item of input) {
        if (Array.isArray(item)) {
          this._flattenArray(item, out);
        } else if (item) {
          out.push(item);
        }
      }
    }
    return out;
  }

  private decodeEntities(encodedString) {
    const translate_re = /&(nbsp|amp|quot|lt|gt);/g;
    const translate = {
      'nbsp': ' ',
      'amp': '&',
      'quot': '\"',
      'lt': '<',
      'gt': '>',
    };
    return encodedString.replace(translate_re, function(match, entity) {
      return translate[entity];
    }).replace(/&#(\d+);/gi, function(match, numStr) {
      const num = parseInt(numStr, 10);
      return String.fromCharCode(num);
    });
  }

  private setFavicon(share, node) {
    const { brandingProfile } = share.instance
    const isCapture = node ? this.CAPTURE_ROLES.includes(node.role) : false;
    const boxPath = this.getAbsoluteBoxPath(share);
    const distPath = this.getAbsoluteDistPath(share);
    let faviconUrl;

    if (brandingProfile && brandingProfile.sharesFaviconStoredFileUUID) {
      faviconUrl = `${ boxPath }/file/${ brandingProfile.sharesFaviconStoredFileUUID }`;
    } else if (isCapture) {
      faviconUrl = `${ distPath }/${ this.NIMBUS_CAPTURE_FAVICON }`;
    } else {
      faviconUrl = `${ distPath }/${ this.NIMBUS_NOTE_FAVICON }`;
    }

    this.updateFavicon(faviconUrl);
  }

  private getMetaUrlForShare (share) {
    const { domain, sub } = share.instance.org;
    const numbusDomain = this._config.get('NIMBUS_DOMAIN');
    if (domain) {
      return `https://${ domain }`;
    } else {
      
      return `https://${ sub }.${ numbusDomain }`;
    }
  }

  private getTitleForNode (share, node) {
    const { org, workspace } = share.instance;
    if (org.domain) {
      return `${ node.title } - ${ workspace.title }`;
    } else {
      const isCapture = this.CAPTURE_ROLES.includes(node.role);
      const appTitle = isCapture ? this.NIMBUS_CAPTURE_TITLE : this.NIMBUS_NOTE_TITLE;
      return `${ node.title } - ${ appTitle }`;
    }
  }

  private getDescriptionForNode (share, node, text?) {
    const { org } = share.instance;
    const isCapture = this.CAPTURE_ROLES.includes(node.role);
    const nodeText = (text && text.textShort) || '';
    if (org.domain) {
      return nodeText;
    } else {
      if (isCapture) {
        return this.NIMBUS_CAPTURE_SLOGAN;
      } else {
        return nodeText || this.NIMBUS_NOTE_SLOGAN;
      }
    }
  }

  private getTitleForAttachment (share, attachment) {
    const { org, workspace } = share.instance;
    const { displayName } = attachment;
    if (org.domain) {
      return `${ displayName } - ${ workspace.title }`;
    } else {
      return `${ displayName } - FuseBase`;
    }
  }

  private getDescriptionForAttachment(share) {
    const { org } = share.instance;
    if (org.domain) {
      return '';
    } else {
      return this.NIMBUS_NOTE_SLOGAN;
    }
  }

  private setMetaUrl (content: string) {
    this.addTags([
      { property: 'og:url', content },
      { name: 'twitter:url', content },
    ]);
  }

  private setMetaPreview (share, node, attachment) {

    const PREVIEW_CARD_SMALL = 'summary';
    const PREVIEW_CARD_LARGE = 'summary_large_image';

    const { org } = share.instance;
    const isB2B = isB2BOrgPredicate()(org)
    const isCapture = this.CAPTURE_ROLES.includes(node.role);
    const boxPath = this.getAbsoluteBoxPath(share);
    const distPath = this.getAbsoluteDistPath(share);

    let previewCard;
    let previewType;
    let previewUrl;
    let previewMime;
    let previewAppName;

    const setupDefault = () => {
      if (org.domain) {
        return;
      } else {
        previewType = 'image';
        previewMime = 'image/png';
        previewCard = PREVIEW_CARD_LARGE;
        if (isCapture) {
          previewUrl = `${ distPath }/${ this.NIMBUS_CAPTURE_PREVIEW }`;
        } else {
          previewUrl = `${ distPath }/${ this.NIMBUS_NOTE_PREVIEW }`;
        }
      }
    }

    if (attachment && attachment.attachmentURL) {
      const { type, mime, extra, attachmentURL } = attachment;
      previewUrl = boxPath + attachmentURL;
      previewMime = mime;
      if (type === 'image') {
        previewType = 'image';
        if (extra && extra.width && extra.width >= 305) {
          previewCard = PREVIEW_CARD_LARGE;
        } else {
          previewCard = PREVIEW_CARD_LARGE;
          // TODO: Use small variant after fixing
          // https://nimbusweb.atlassian.net/browse/NIM-10939
          // previewCard = PREVIEW_CARD_SMALL;
        }
      } else if (type === 'video' && (mime === 'video/mp4' || mime === 'video/webm')) {
        previewType = 'video';
        previewCard = PREVIEW_CARD_LARGE;
      } else {
        setupDefault();
      }
    } else {
      setupDefault();
    }

    if (isB2B) {
      previewAppName = org.title
    } else if (isCapture) {
      previewAppName = this.NIMBUS_CAPTURE_TITLE
    } else {
      previewAppName = this.NIMBUS_NOTE_TITLE
    }

    this.addTags([
      { name: 'application-name', content: previewAppName },
      { name: 'twitter:card', content: previewCard },
    ])

    if (previewCard && previewUrl && previewType) {
      if (previewType === 'image') {
        this.addTags([
          { property: 'og:image', content: previewUrl },
          { property: 'og:image:url', content: previewUrl },
          { property: 'og:image:type', content: previewMime },
          { name: 'twitter:image', content: previewUrl },
        ]);
      }
      if (previewType === 'video') {
        const defaultImageUrl = `${distPath}/${this.NIMBUS_NOTE_PREVIEW}`;
        this.addTags([
          { property: 'og:video', content: previewUrl },
          { property: 'og:video:url', content: previewUrl },
          { property: 'og:video:type', content: previewMime },
          { name: 'twitter:video', content: previewUrl },
          { property: 'og:image', content: defaultImageUrl },
          { property: 'og:image:url', content: defaultImageUrl },
          { property: 'og:image:type', content: 'image/png' },
          { name: 'twitter:image', content: defaultImageUrl },
        ]);
      }
    }
  }

  private setMetaTitle(content: string) {
    const decodedContent = this.decodeEntities(content);
    this.title.setTitle(decodedContent);
    this.addTags([
      { property: 'og:title', content: decodedContent },
      { name: 'twitter:title', content: decodedContent },
    ]);
  }

  private setMetaEmbedType(node) {
    if(node && node.role) {
      const role = node.role;
      if(role == this.CAPTURE_ROLES[1] || role == this.CAPTURE_ROLES[2] || role == this.CAPTURE_ROLES[3]) {
        this.addTags([
          { name: 'nimbus:embedType', content: 'video' },
        ]);
      } else {
        this.addTags([
          { name: 'nimbus:embedType', content: 'default' },
        ]);
      }
    }
  }

  private setMetaDescription(content: string) {
    const decodedContent = this.decodeEntities(content);
    this.addTags([
      { property: 'og:description', content: decodedContent },
      { name: 'twitter:description', content: decodedContent },
    ]);
  }

  private getShareUrlBase (share) {
    const { org } = share.instance;
    const nimbusUrl = this._config.get('NIMBUS_URL');
    let urlBase;
    if (!org) {
      urlBase = nimbusUrl;
    } else if (org.domain) {
      urlBase = `https://${ org.domain }`;
    } else if (org.sub) {
      const nimbusDomain = this._config.get('NIMBUS_DOMAIN');
      urlBase = `https://${ org.sub }.${ nimbusDomain }`
    }
    return urlBase;
  }

  private getAbsoluteBoxPath (share) {
    const shareUrlBase = this.getShareUrlBase(share);
    const boxPath = this._config.get('BOX_URL');
    return boxPath.startsWith('http') ? boxPath : shareUrlBase + boxPath;
  }

  private getAbsoluteDistPath (share) {
    const shareUrlBase = this.getShareUrlBase(share);
    const distPath = '/share/dist';
    return shareUrlBase + distPath;
  }

  private updateFavicon(url) {
    const link: any = this._document.querySelector('link[rel*="icon"]') || this._document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = url;
    this._document.getElementsByTagName('head')[0].appendChild(link);
  }

}
